import * as Sentry from '@sentry/react';

/** Structural stand-in for wagmi's `CreateConnectorFn` — ui-kit has no wagmi dependency. */
type CreateConnectorFnLike = (config: never) => { id?: string; setup?: () => Promise<void> };

/**
 * `createConfig()` calls `connector.setup()` and discards the promise, so whatever it rejects with
 * escapes as an unhandled rejection that no `try`/`catch` around `createConfig()` can reach — V8's
 * async stack traces just make it look synchronous in Sentry.
 *
 * `setup()` reads the injected provider straight away, and an extension whose provider is a Proxy
 * with a `get` trap that violates the invariants for a frozen property makes that read throw
 * (`'get' on proxy: property 'removeListener' is a read-only and non-configurable data property…`).
 * Such a provider is unusable however it is reached, so report it as handled and move on: the page
 * renders and the other wallets keep working.
 */
export function guardConnectorSetup<Fn extends CreateConnectorFnLike>(createConnectorFn: Fn): Fn {
  return ((config: never) => {
    const connector = createConnectorFn(config);
    const setup = connector.setup;
    if (!setup) return connector;

    // Replaced in place — `createConfig()` spreads this object onto the real connector straight
    // after, so `this` is the finished connector and cloning here would only duplicate that work.
    connector.setup = function guardedSetup(this: { id?: string }) {
      return setup.call(this).catch((error: unknown) => {
        Sentry.captureException(error, {
          level: 'warning',
          extra: { context: 'wallet_connector_setup', connectorId: this.id },
        });
      });
    };
    return connector;
  }) as Fn;
}
